/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState, useRef } from "react";
import Card from "components/card";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { MdCheckCircle, MdCancel, MdOutlineError } from "react-icons/md";
import { useMemo } from "react";
import { format } from "date-fns";
import { useGlobalContext } from "context/globalContext";
import { getFilterPromptVariablesAPI } from "Api/promptVariablesAPI";
import { getPromptVariablesAPI } from "Api/promptVariablesAPI";
import CreatePromptVariablesBtn from "../create-prompt-variables/createPromptVariables";
import EditOrDeletePromptVariables from "../edit-or-delete-prompt-variables/editOrDeletePromptVariablesModal";
import Loader from "components/loader/Loader";

const PromptVariablesTable = () => {
  const { fetchPromptVariables, setFetchPromptVariables } = useGlobalContext();
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterTableData, setFilterTableData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);

  const debounceTimeoutRef = useRef(null);

  const handleSearchQuery = useCallback((value) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPageIndex(0);
    }, 300);
  }, []);

  // const handleSearchQuery = handleDebounceSearchQuery();

  const getFilterPromptsVariables = useCallback(async () => {
    if (!searchQuery) return;
    const payload = { searchQuery };
    const token = localStorage.getItem("jwtToken");

    const skip = pageIndex * 10;
    const limit = 10;

    const response = await getFilterPromptVariablesAPI(
      { searchQuery, pageIndex, limit, skip },
      payload,
      setIsLoading,
      token
    );
    if (response) {
      setFilterTableData(response.data);
      setTotalPages(response.totalPages);
      setFetchPromptVariables(false);
    }
    if (!response) {
      setFilterTableData([]);
    }
  }, [searchQuery, pageIndex, setFetchPromptVariables]);

  const getPromptVariables = useCallback(async () => {
    if (searchQuery !== "") return;
    const token = localStorage.getItem("jwtToken");
    const skip = 10;
    const response = await getPromptVariablesAPI(
      {
        skip,
        pageIndex,
      },
      setIsLoading,
      token
    );
  if (response) {
  if (response.data.length === 0 && pageIndex > 0) {
    setPageIndex((prev) => prev - 1); 
  } else {
    setTableData(response.data);
    setTotalPages(response?.pagination?.totalPages || 0);
    setFetchPromptVariables(false);
  }
}
  }, [searchQuery, setFetchPromptVariables, pageIndex]);

  useEffect(() => {
    if (searchQuery === "") {
      getPromptVariables();
    } else {
      getFilterPromptsVariables();
    }
  }, [searchQuery, pageIndex, getPromptVariables, getFilterPromptsVariables]);

  useEffect(() => {
    if (fetchPromptVariables) {
      if (searchQuery === "") {
        getPromptVariables();
      } else {
        getFilterPromptsVariables();
      }
      setFetchPromptVariables(false);
    }
  }, [
    fetchPromptVariables,
    searchQuery,
    getPromptVariables,
    getFilterPromptsVariables,
  ]);

  const columnsData = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Created At",
        accessor: "createdAt",
      },
      {
        Header: "Actions",
        accessor: "actions",
      },
    ],
    []
  );

  const columns = useMemo(() => columnsData, [columnsData]);

  const tableInstance = useTable(
    {
      columns,
      data: searchQuery === "" ? tableData : filterTableData,
      manualPagination: true,
      pageCount: totalPages,
      pageIndex,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,

    gotoPage,
  } = tableInstance;

  const handlePageChange = (direction) => {
    if (direction === "next" && pageIndex < totalPages - 1) {
      setPageIndex((prevPageIndex) => {
        const newPageIndex = prevPageIndex + 1;
        gotoPage(newPageIndex);
        return newPageIndex;
      });
    }

    if (direction === "previous" && pageIndex > 0) {
      setPageIndex((prevPageIndex) => {
        const newPageIndex = prevPageIndex - 1;
        gotoPage(newPageIndex);
        return newPageIndex;
      });
    }
  };

  return (
    <Card extra={"w-full h-full p-4 sm:overflow-x-auto "}>
      <div className="relative flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex w-full flex-col gap-3 sm:max-w-lg sm:flex-row">
          <div className="relative w-full sm:w-64">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-lg leading-none text-gray-400">
              🔍
            </span>
            <input
              type="search"
              className="w-full rounded-md border border-gray-300 py-2 pl-8 pr-2 text-sm transition-all duration-300 ease-in-out hover:border-blue-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white "
              placeholder="Search"
              onChange={(e) => handleSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <CreatePromptVariablesBtn />
      </div>

      <div className="mt-4 h-full overflow-x-scroll xl:overflow-auto">
        {isLoading && tableData.length === 0 && (
          <div className="flex h-[400px] w-full items-center justify-center">
            <Loader />
          </div>
        )}
        {!isLoading &&
          (searchQuery === ""
            ? tableData.length === 0
            : filterTableData.length === 0) && (
            <span className="block w-full text-center"> No data found.</span>
          )}

        {(searchQuery ? filterTableData : tableData).length > 0 && (
          <table {...getTableProps()} className="w-full">
            <thead>
              {headerGroups.map((headerGroup, index) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <th
                     
                      key={index}
                      className="border-b border-gray-200 pr-20 pb-[10px] text-start dark:!border-navy-700"
                    >
                      <p className="w-72 text-xs tracking-wide text-gray-600">
                        {column.render("Header")}
                      </p>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={index}>
                    {row.cells.map((cell, index) => {
                      let data = cell.render("Cell");

                      if (cell.column.Header === "Status") {
                        data = (
                          <div className="flex items-center">
                            <div className={`rounded-full text-xl`}>
                              {cell.value === true ? (
                                <MdCheckCircle className="text-green-500" />
                              ) : cell.value === false ? (
                                <MdCancel className="text-red-500" />
                              ) : cell.value === "Error" ? (
                                <MdOutlineError className="text-orange-500" />
                              ) : null}
                            </div>
                          </div>
                        );
                      } else if (cell.column.Header === "Created At") {
                        data = format(
                          new Date(cell.value || null),
                          "yyyy-MM-dd HH:mm:ss"
                        );
                      } else if (cell.column.Header === "Actions") {
                        data = (
                          <EditOrDeletePromptVariables
                            value={cell.value}
                            cell={cell}
                          />
                        );
                      }

                      return (
                        <td
                          className="px-[7px] pt-[14px] pb-[8px] sm:text-[14px]"
                          {...cell.getCellProps()}
                          key={index}
                        >
                          <p>{data}</p>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {((!!tableData.length && !searchQuery) ||
          (!!filterTableData.length && searchQuery)) && (
          <div className="mt-4 flex items-center justify-between">
            <button
              onClick={() => handlePageChange("previous")}
              disabled={pageIndex === 0}
              className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                pageIndex > 0
                  ? "bg-blue-500 hover:bg-blue-600"
                  : "cursor-not-allowed bg-gray-300"
              }`}
            >
              Previous
            </button>

            <span className="text-sm text-gray-700 dark:text-white">
              Page <strong>{pageIndex + 1}</strong> of{" "}
              <strong>{totalPages}</strong>
            </span>

            <button
              onClick={() => handlePageChange("next")}
              disabled={pageIndex >= totalPages - 1}
              className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                pageIndex < totalPages - 1
                  ? "bg-blue-500 hover:bg-blue-600"
                  : "cursor-not-allowed bg-gray-300"
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default PromptVariablesTable;
